import * as validators from '@vuelidate/validators';

const { createI18nMessage } = validators;

const loaders: Map<String, Array<{ key: String, promise: Promise<Boolean> }>> = new Map();
const getLoader = (locale: String) => (loaders.has(locale)
    ? loaders.get(locale)
    : loaders.set(locale, []).get(locale)) as Object[];

const loaded: Map<String, Array<String>> = new Map();
const getLoaded = (locale: String) => (loaded.has(locale)
    ? loaded.get(locale)
    : loaded.set(locale, []).get(locale)) as String[];

export default defineNuxtPlugin((nuxtApp: NuxtApp) => {
    console.log('[I18n]: Init.');
    return {
        provide: {
            translations: async (key: unknown) => {
                if (typeof key === 'string') {
                    return await nuxtApp.$translation.load(key);
                } else if (Array.isArray(key)) {
                    return await nuxtApp.$translation.loadSearch(key);
                }
            },
            validator: async (name: String, params?: any) => {
                nuxtApp.$translations(`$pipeline.app.validation.${name}`);

                const withI18nMessage = createI18nMessage({ t: nuxtApp.$i18n.t, messagePath: ({ $validator }) => `$pipeline.app.validation.${$validator}` });
                const validator = validators[name];

                if (name === 'minLength') {
                    return withI18nMessage(validator(params?.min));
                } else {
                    return withI18nMessage(validator);
                }
            },
            regexValidator: async (name: String, regexp: () => RegExp) => {
                nuxtApp.$translations(`$pipeline.app.validation.${name}`);
                const withI18nMessage = createI18nMessage({ t: nuxtApp.$i18n.t, messagePath: () => `$pipeline.app.validation.${name}` });
                return withI18nMessage((value: string) => validators.helpers.regex(regexp())(value));
            },
            translation: {
                reassert: async (to: string) => {
                    const from: string = nuxtApp.$i18n.locale.value;
                    const fromKeys = getLoader(from).map(x => x.key);
                    const toKeys = getLoader(to).map(x => x.key);
                    const unloadedKeys = fromKeys.filter(key => !toKeys.includes(key));

                    const promises = unloadedKeys.map(async (key: String) => new Promise((resolve, reject) => {
                        resolve(nuxtApp.$translation.load(key, to));
                    }));

                    return Promise.all(promises);
                },
                loadSearch: async (keys: Array<String>, locale: string = nuxtApp.$i18n.locale.value) => {
                    const loadedKeys = getLoader(locale).map(x => x.key);
                    const unloadedKeys = keys.filter(key => !loadedKeys.includes(key));

                    if (unloadedKeys.length === 0) return new Promise((resolve) => resolve(true));
                    
                    const data = await $fetch(`/api/${locale}/translation`, {
                        method: 'POST',
                        headers: {
                            Accept: 'application/json',
                            'Cache-Control': 'no-cache',
                            'X-Requested-With': 'pipeline-client-2',
                            'Content-Type': 'application/json'
                        },
                        body: JSON.stringify(unloadedKeys)
                    });

                    return new Promise((resolve, reject) => {
                        const loaders = getLoader(locale);

                        nuxtApp.$i18n.mergeLocaleMessage(locale, data);
                        unloadedKeys.forEach(key => loaders.push({
                            key: key,
                            promise: Promise.resolve(true)
                        }));
                        
                        resolve(true);
                    });
                },
                load: async (key: String, locale: string = nuxtApp.$i18n.locale.value) => {
                    const loaders = getLoader(locale);
                    let current = loaders.find(x => x.key === key);

                    if (current) {
                        return await current.promise;
                    }

                    current = {
                        key: key,
                        promise: new Promise<Boolean>(async (resolve) => {
                            const data = await $fetch(`/api/${locale}/translation/${key}`, {
                                method: 'GET',
                                headers: {
                                    Accept: 'application/json',
                                    'Cache-Control': 'no-cache',
                                    'X-Requested-With': 'pipeline-client-2',
                                    'Content-Type': 'application/json'
                                }
                            });

                            nuxtApp.$i18n.mergeLocaleMessage(locale, data);

                            resolve(true);
                        })
                    };

                    loaders.push({
                        key: key,
                        promise: current
                    })

                    return await current.promise;
                }
            }
        }
    }
})